import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import GoogleMaps from "../../../../GoogleMaps";
import Card from "../../../../Card";
import { UIButton } from "../../../../ui";
import messages from "../../../../../assets/locale/messages";
import "../../_EventDetailFields.scss";

import "./PaidTrainingSession.scss";

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY , hh:mm A") : "-";
};

const InfoField = ({ label, children, size, boxed }) => (
  <Grid item xs={12} md={size}>
    <div className="event-detail-field">
      <span className="event-detail-label">{label}</span>
      {boxed ? (
        <div className="event-detail-value-box">
          <span className="event-detail-value">
            {hasValue(children) ? children : "-"}
          </span>
        </div>
      ) : (
        <span className="event-detail-value">
          {hasValue(children) ? children : "-"}
        </span>
      )}
    </div>
  </Grid>
);

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  size: PropTypes.number,
  boxed: PropTypes.bool,
};

InfoField.defaultProps = {
  children: null,
  size: 4,
  boxed: false,
};

const MarginPanel = ({
  title,
  beforeLabel,
  beforeValue,
  afterLabel,
  afterValue,
  tone,
}) => (
  <Grid item xs={12} md={6}>
    <div className={`event-detail-margin-box event-detail-margin-box--${tone}`}>
      <span className="event-detail-label">{title}</span>
      <div className="event-detail-field">
        <span className={`event-detail-label event-detail-label-${tone}`}>{beforeLabel}</span>
        <span className="event-detail-value">
          {hasValue(beforeValue) ? beforeValue : "-"}
        </span>
      </div>
      <div className="event-detail-field">
        <span className={`event-detail-label event-detail-label-${tone}`}>{afterLabel}</span>
        <span className="event-detail-value">
          {hasValue(afterValue) ? afterValue : "-"}
        </span>
      </div>
    </div>
  </Grid>
);

MarginPanel.propTypes = {
  title: PropTypes.string.isRequired,
  beforeLabel: PropTypes.string.isRequired,
  beforeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  afterLabel: PropTypes.string.isRequired,
  afterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tone: PropTypes.oneOf(["success", "danger"]).isRequired,
};

MarginPanel.defaultProps = {
  beforeValue: null,
  afterValue: null,
};

const PaidTrainingSession = ({ training, portalVariant }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events.viewEvents;
  const trainingLabels = t.paidTraining;
  const createLabels = events.creatEvent.defineRolesAndShifts;
  const formLabels = events.creatEvent.labels;
  // The training's payroll figure is an internal cost never exposed to the client.
  const showTrainingPayroll = portalVariant !== "client-portal";
  const infoSize = showTrainingPayroll ? 4 : 6;

  if (!training || training?._destroy) return null;

  const hasCoords = Boolean(training?.lat && training?.lng);
  const hasQrCode = Boolean(training?.qr_code_url);

  const handlePrint = useCallback(() => {
    const win = window.open("", "_blank");
    if (!win) return;
    const img = win.document.createElement("img");
    img.src = training.qr_code_url;
    img.style.cssText = "max-width:400px;display:block;margin:auto;";
    img.addEventListener("load", () => {
      win.print();
      win.close();
    });
    win.document.body.style.cssText =
      "margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;";
    win.document.body.appendChild(img);
  }, [training?.qr_code_url]);

  const mapCoords = hasCoords
    ? {
        coords: {
          lat: Number(training.lat),
          lng: Number(training.lng),
        },
      }
    : null;

  return (
    <Box className="paid-training-session">
      <div className="paid-training-session__header">
        <span className="event-detail-block-title">{createLabels.paidTraining}</span>
      </div>

      <Box className="paid-training-session__card">
        <Grid container spacing={3}>
          <InfoField label={trainingLabels.trainingName} size={infoSize}>
            {training?.name}
          </InfoField>
          {showTrainingPayroll && (
            <InfoField label={createLabels.trainingPaidAmountSar}>
              {training?.payroll_amount}
            </InfoField>
          )}
          <InfoField label={trainingLabels.numberOfDays} size={infoSize}>
            {training?.number_of_days}
          </InfoField>

          <InfoField label={trainingLabels.trainingDescription} size={12}>
            {training?.description}
          </InfoField>

          <Grid item xs={12}>
            <div className="paid-training-session__timeline">
              <span className="event-detail-label">
                {createLabels.trainingTimeline}
                <span className="paid-training-session__required">*</span>
              </span>
              <Grid container spacing={3}>
                <InfoField label={formLabels.trainingStartDate} size={6} boxed>
                  {formatDateTime(training?.starts_at)}
                </InfoField>
                <InfoField label={formLabels.trainingEndDate} size={6} boxed>
                  {formatDateTime(training?.ends_at)}
                </InfoField>
              </Grid>
            </div>
          </Grid>

          <MarginPanel
            title={trainingLabels.checkInMargin}
            beforeLabel={trainingLabels.checkInBeforeMargin}
            beforeValue={training?.before_check_in_margin}
            afterLabel={trainingLabels.checkInAfterMargin}
            afterValue={training?.after_check_in_margin}
            tone="success"
          />
          <MarginPanel
            title={trainingLabels.checkOutMargin}
            beforeLabel={trainingLabels.checkOutBeforeMargin}
            beforeValue={training?.before_check_out_margin}
            afterLabel={trainingLabels.checkOutAfterMargin}
            afterValue={training?.after_check_out_margin}
            tone="danger"
          />

          <InfoField label={trainingLabels.location} size={6}>
            {training?.location_address || training?.address || t.noLocation}
          </InfoField>
          <InfoField label={trainingLabels.locationBoundaries} size={6}>
            {hasValue(training?.location_radius)
              ? training.location_radius
              : "-"}
          </InfoField>

          {(hasCoords || hasQrCode) && (
            <>
              {hasCoords && (
                <Grid item xs={12} md={hasQrCode ? 8 : 12}>
                  <Card
                    title={t.trainingLocation}
                    classNames={{
                      root: "paid-training-session__map-card",
                      body: "paid-training-session__map-body",
                    }}
                  >
                    <GoogleMaps
                      viewOnly
                      isMarker
                      showCircle={hasValue(training?.location_radius)}
                      circleRadius={Number(training?.location_radius) || 0}
                      savedCoords={mapCoords}
                      updatedPosition={mapCoords}
                      wrapperClasses="paid-training-session__map-wrapper"
                      className="paid-training-session__map"
                      getCoords={() => {}}
                      markerMoved={() => {}}
                    />
                  </Card>
                </Grid>
              )}

              {hasQrCode && (
                <Grid item xs={12} md={hasCoords ? 4 : 12}>
                  <Card
                    title={trainingLabels.trainingAttendanceQrCode}
                    classNames={{
                      root: "paid-training-session__qr-card",
                      body: "paid-training-session__qr-body",
                    }}
                  >
                    <img
                      src={training.qr_code_url}
                      alt={trainingLabels.trainingAttendanceQrCode}
                      className="paid-training-session__qr-image"
                    />
                    <Box className="paid-training-session__qr-actions">
                      <UIButton
                        variant="outlined"
                        size="medium"
                        className="paid-training-session__qr-btn"
                        onClick={handlePrint}
                      >
                        {trainingLabels.print}
                      </UIButton>
                      <UIButton
                        variant="outlined"
                        size="medium"
                        className="paid-training-session__qr-btn"
                        component="a"
                        href={training.qr_code_url}
                        download
                        target="_blank"
                        rel="noreferrer"
                      >
                        {shared.download}
                      </UIButton>
                    </Box>
                  </Card>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

PaidTrainingSession.propTypes = {
  training: PropTypes.object,
  portalVariant: PropTypes.oneOf(["company-portal", "client-portal"]),
};

PaidTrainingSession.defaultProps = {
  training: null,
  portalVariant: "company-portal",
};

export default PaidTrainingSession;
